import {
  PanelBody,
  SelectControl,
  TextControl,
  ToggleControl,
  FormTokenField,
  RangeControl,
  __experimentalNumberControl as NumberControl,
} from "@wordpress/components";
import { store as coreStore } from "@wordpress/core-data";
import { useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";

// WP_Query resolves these itself, so the list is what the UI offers rather than
// a whitelist: `orderBy` is passed straight through by
// build_query_vars_from_query_block(), the same as in the Query Loop.
const orderByOptions = [
  { label: __("Newest first", "brandy-blocks"), value: "date" },
  { label: __("Last modified", "brandy-blocks"), value: "modified" },
  { label: __("Title", "brandy-blocks"), value: "title" },
  { label: __("Menu order", "brandy-blocks"), value: "menu_order" },
  { label: __("Comment count", "brandy-blocks"), value: "comment_count" },
  { label: __("Random", "brandy-blocks"), value: "rand" },
];

const orderOptions = [
  { label: __("Descending", "brandy-blocks"), value: "desc" },
  { label: __("Ascending", "brandy-blocks"), value: "asc" },
];

const stickyOptions = [
  { label: __("Include", "brandy-blocks"), value: "" },
  { label: __("Exclude", "brandy-blocks"), value: "exclude" },
  { label: __("Only sticky posts", "brandy-blocks"), value: "only" },
  { label: __("Ignore sticky (no pinning)", "brandy-blocks"), value: "ignore" },
];

/**
 * Term picker backed by FormTokenField. `taxQuery` stores term IDs (that is what
 * core's query builder wants); the field speaks names, so the two are mapped here.
 */
function TermTokenField({ taxonomy, label, value = [], onChange }) {
  const terms = useSelect(
    (select) =>
      select(coreStore).getEntityRecords("taxonomy", taxonomy, {
        per_page: -1,
        _fields: "id,name",
        context: "view",
      }),
    [taxonomy]
  );

  const suggestions = (terms ?? []).map((term) => term.name);
  const selectedNames = (value ?? [])
    .map((id) => (terms ?? []).find((term) => term.id === id)?.name)
    .filter(Boolean);

  return (
    <FormTokenField
      __next40pxDefaultSize
      __nextHasNoMarginBottom
      label={label}
      value={selectedNames}
      suggestions={suggestions}
      onChange={(names) =>
        onChange(
          names
            .map((name) => (terms ?? []).find((term) => term.name === name)?.id)
            .filter((id) => typeof id === "number")
        )
      }
    />
  );
}

export default function QueryPanel({ query, updateQuery }) {
  const inherit = !!query.inherit;
  const postType = query.postType ?? "post";

  const { postTypes, taxonomies, users } = useSelect((select) => {
    const { getPostTypes, getTaxonomies, getUsers } = select(coreStore);

    return {
      postTypes: getPostTypes({ per_page: -1 }),
      taxonomies: getTaxonomies({ type: postType, per_page: -1 }),
      users: getUsers({ per_page: -1, _fields: "id,name" }),
    };
  }, [postType]);

  // Only types a visitor can reach on the frontend; `attachment` is viewable but
  // is never what a posts loop means.
  const postTypeOptions = (postTypes ?? [])
    .filter((type) => type.viewable && "attachment" !== type.slug)
    .map((type) => ({ label: type.labels?.singular_name ?? type.name, value: type.slug }));

  // Same gate core applies server-side in build_query_vars_from_query_block(),
  // which drops clauses for taxonomies that are not publicly queryable.
  const termTaxonomies = (taxonomies ?? []).filter(
    (taxonomy) => taxonomy.visibility?.publicly_queryable
  );

  const taxQuery = query.taxQuery ?? {};
  const updateTaxQuery = (slug, ids) => {
    const next = { ...taxQuery, [slug]: ids };
    if (!ids.length) {
      delete next[slug];
    }
    updateQuery({ taxQuery: Object.keys(next).length ? next : null });
  };

  const authorIds = String(query.author ?? "")
    .split(",")
    .map((id) => parseInt(id, 10))
    .filter(Boolean);

  return (
    <PanelBody title={__("Posts", "brandy-blocks")}>
      <ToggleControl
        __nextHasNoMarginBottom
        label={__("Inherit query from template", "brandy-blocks")}
        help={__(
          "On an archive, search or home template, show that page's own results — including its filtering and paging. The controls below do not apply.",
          "brandy-blocks"
        )}
        checked={inherit}
        onChange={(value) => updateQuery({ inherit: value })}
      />
      {inherit ? null : (
        <>
          <SelectControl
            __nextHasNoMarginBottom
            __next40pxDefaultSize
            label={__("Post type", "brandy-blocks")}
            value={postType}
            options={
              postTypeOptions.length
                ? postTypeOptions
                : [{ label: __("Post", "brandy-blocks"), value: "post" }]
            }
            onChange={(value) =>
              // Term selections belong to the old type's taxonomies, so they go
              // with it rather than silently filtering the new type by nothing.
              updateQuery({ postType: value, taxQuery: null, sticky: "" })
            }
          />
          <RangeControl
            __nextHasNoMarginBottom
            __next40pxDefaultSize
            label={__("Posts per page", "brandy-blocks")}
            value={query.perPage ?? 8}
            min={1}
            max={50}
            onChange={(value) => updateQuery({ perPage: value })}
          />
          <NumberControl
            __next40pxDefaultSize
            label={__("Offset", "brandy-blocks")}
            help={__(
              "Skip this many posts before the first one shown.",
              "brandy-blocks"
            )}
            value={query.offset ?? 0}
            min={0}
            onChange={(value) =>
              updateQuery({ offset: parseInt(value, 10) || 0 })
            }
          />
          <NumberControl
            __next40pxDefaultSize
            label={__("Max pages", "brandy-blocks")}
            help={__(
              "Caps how many pages the Pagination block offers. 0 shows them all.",
              "brandy-blocks"
            )}
            value={query.pages ?? 0}
            min={0}
            onChange={(value) => updateQuery({ pages: parseInt(value, 10) || 0 })}
          />
          <SelectControl
            __nextHasNoMarginBottom
            __next40pxDefaultSize
            label={__("Order by", "brandy-blocks")}
            value={query.orderBy ?? "date"}
            options={orderByOptions}
            onChange={(value) => updateQuery({ orderBy: value })}
          />
          {"rand" !== (query.orderBy ?? "date") && (
            <SelectControl
              __nextHasNoMarginBottom
              __next40pxDefaultSize
              label={__("Order", "brandy-blocks")}
              value={query.order ?? "desc"}
              options={orderOptions}
              onChange={(value) => updateQuery({ order: value })}
            />
          )}
          {termTaxonomies.map((taxonomy) => (
            <TermTokenField
              key={taxonomy.slug}
              taxonomy={taxonomy.slug}
              label={taxonomy.name}
              value={taxQuery[taxonomy.slug]}
              onChange={(ids) => updateTaxQuery(taxonomy.slug, ids)}
            />
          ))}
          <FormTokenField
            __next40pxDefaultSize
            __nextHasNoMarginBottom
            label={__("Authors", "brandy-blocks")}
            value={authorIds
              .map((id) => (users ?? []).find((user) => user.id === id)?.name)
              .filter(Boolean)}
            suggestions={(users ?? []).map((user) => user.name)}
            onChange={(names) =>
              updateQuery({
                // Stored as the comma-separated string core's builder reads.
                author: names
                  .map((name) => (users ?? []).find((user) => user.name === name)?.id)
                  .filter(Boolean)
                  .join(","),
              })
            }
          />
          <TextControl
            __next40pxDefaultSize
            __nextHasNoMarginBottom
            label={__("Keyword", "brandy-blocks")}
            value={query.search ?? ""}
            onChange={(value) => updateQuery({ search: value })}
          />
          {"post" === postType && (
            <SelectControl
              __nextHasNoMarginBottom
              __next40pxDefaultSize
              label={__("Sticky posts", "brandy-blocks")}
              value={query.sticky ?? ""}
              options={stickyOptions}
              onChange={(value) => updateQuery({ sticky: value })}
            />
          )}
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Exclude the current post", "brandy-blocks")}
            help={__(
              "Useful for a related-posts row on a single post.",
              "brandy-blocks"
            )}
            checked={!!query.excludeCurrent}
            onChange={(value) => updateQuery({ excludeCurrent: value })}
          />
        </>
      )}
    </PanelBody>
  );
}
